// Chart configuration shared by every audience histogram view: the composition
// dashboard, the execution detail modal and the composition modal. Kept as plain
// functions so the configuration can be unit tested without a canvas.

// Blues, teals and greens matching the Aqfer brand
export const CHART_COLORS = [
  'rgba(44, 123, 229, 0.85)',   // Primary blue
  'rgba(32, 178, 170, 0.85)',   // Teal
  'rgba(46, 184, 92, 0.85)',    // Green
  'rgba(64, 145, 210, 0.85)',   // Sky blue
  'rgba(0, 168, 150, 0.85)',    // Cyan teal
  'rgba(72, 199, 142, 0.85)',   // Sea green
  'rgba(52, 152, 219, 0.85)',   // Soft blue
  'rgba(26, 188, 156, 0.85)',   // Turquoise
  'rgba(39, 174, 96, 0.85)',    // Emerald
  'rgba(86, 165, 215, 0.85)',   // Light blue
  'rgba(0, 150, 136, 0.85)',    // Deep teal
  'rgba(102, 187, 106, 0.85)',  // Light green
  'rgba(100, 181, 246, 0.85)',  // Pale blue
  'rgba(77, 182, 172, 0.85)',   // Medium teal
  'rgba(129, 199, 132, 0.85)',  // Soft green
]

// High contrast orange, for values the user has picked out
export const HIGHLIGHT_COLOR = '#FF8C00'
export const HIGHLIGHT_BORDER = '#E67300'

// Past this a tile is unreadable, and the long tail is in the table anyway
export const MAX_CHART_VALUES = 15

export function formatAttributeName(name) {
  if (!name) return ''
  return String(name)
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function chartTypeFor(attr) {
  // A doughnut stops being readable once the slices get thin
  return (attr?.values?.length || 0) <= 6 ? 'Doughnut' : 'Bar'
}

export function chartSizeClassFor(attr) {
  const count = attr?.values?.length || 0
  if (count <= 2) return 'chart-xs'
  if (count <= 5) return 'chart-sm'
  if (count <= 10) return 'chart-md'
  if (count <= 20) return 'chart-lg'
  return 'chart-xl'
}

export function chartHeightFor(attr) {
  switch (chartSizeClassFor(attr)) {
    case 'chart-lg': return { height: '200px' }
    case 'chart-xl': return { height: '240px' }
    case 'chart-md': return { height: '140px' }
    default: return { height: '110px' }
  }
}

export function chartValuesFor(attr) {
  return (attr?.values || []).slice(0, MAX_CHART_VALUES)
}

function columnTotal(attr) {
  return (attr?.values || []).reduce((sum, item) => sum + (item.count || 0), 0)
}

function percentage(value, total) {
  if (!total) return 0
  return (value / total) * 100
}

// Single-select columns (gender, age, income) divide by the column's own counts:
// a doughnut's slices always fill the circle, so any other denominator would put
// a percentage on screen that the arc it labels does not match. Those columns sum
// to the record count anyway, so the two agree.
//
// Multi-select columns (interests, media) do not: one record carries many
// interests, so the counts add up well past the number of records. Sharing a
// value out of that sum answers a question nobody asked - "of all interest
// mentions" - and turns 1,046 of 1,515 records from 69% into about 2%. Those
// columns always have enough values to render as a bar, where length encodes the
// count and nothing has to fill a circle, so they divide by the record count.
export function chartDenominator(attr, recordTotal = 0) {
  const sum = columnTotal(attr)
  const multiSelect = recordTotal > 0 && sum > recordTotal
  return {
    total: multiSelect ? recordTotal : sum,
    unit: multiSelect ? 'of records' : 'of known values',
  }
}

export function buildChartData(attr, { isSelected } = {}) {
  const values = chartValuesFor(attr)
  const selected = values.map(item => (isSelected ? !!isSelected(item.value) : false))

  const labels = values.map(item => {
    const label = String(item.value || '(empty)')
    return label.length > 20 ? `${label.substring(0, 17)}...` : label
  })
  const backgroundColor = values.map((item, i) => (
    selected[i] ? HIGHLIGHT_COLOR : CHART_COLORS[i % CHART_COLORS.length]
  ))

  if (chartTypeFor(attr) === 'Doughnut') {
    return {
      labels,
      datasets: [{
        data: values.map(item => item.count),
        backgroundColor,
        borderWidth: selected.map(on => (on ? 3 : 2)),
        borderColor: selected.map(on => (on ? HIGHLIGHT_BORDER : '#fff')),
      }],
    }
  }

  return {
    labels,
    datasets: [{
      data: values.map(item => item.count),
      backgroundColor,
      borderWidth: selected.map(on => (on ? 2 : 0)),
      borderColor: selected.map(on => (on ? HIGHLIGHT_BORDER : '#fff')),
      borderRadius: 4,
    }],
  }
}

export function buildChartOptions(attr, { darkMode = false, animate = true, recordTotal = 0 } = {}) {
  const { total, unit } = chartDenominator(attr, recordTotal)
  const tickColor = darkMode ? 'white' : 'black'
  const describe = (value) => (
    `${value.toLocaleString()} (${percentage(value, total).toFixed(1)}% ${unit})`
  )

  if (chartTypeFor(attr) === 'Doughnut') {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: animate ? undefined : false,
      cutout: '45%',
      plugins: {
        legend: {
          display: true,
          position: 'right',
          labels: {
            color: tickColor,
            boxWidth: 8,
            padding: 4,
            font: { size: 9 },
            generateLabels: (chart) => chart.data.labels.map((label, i) => ({
              text: `${label} ${percentage(chart.data.datasets[0].data[i], total).toFixed(0)}%`,
              fillStyle: chart.data.datasets[0].backgroundColor[i],
              hidden: false,
              fontColor: tickColor,
              index: i,
            })),
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => describe(context.raw),
          },
        },
      },
    }
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: animate ? undefined : false,
    indexAxis: 'y',
    layout: {
      padding: { left: 0, right: 4, top: 0, bottom: 0 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => describe(context.raw),
          // The axis tick is truncated, the tooltip title is not
          title: (items) => chartValuesFor(attr)[items[0].dataIndex]?.value || '(empty)',
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          color: tickColor,
          font: { size: 9 },
          maxTicksLimit: 4,
          callback: (value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`
            if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
            return value
          },
        },
      },
      y: {
        display: true,
        grid: { display: false },
        ticks: { color: tickColor, font: { size: 9 } },
      },
    },
  }
}
